import React, {useEffect, ReactElement, useCallback, useState, cloneElement} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Popper} from '@mui/material';

import FormButton, {Props as ButtonProps} from 'src/components/button';
import {Store} from 'src/types/store';
import {createPopover, destroyPopover} from '../../../shared/actions/popovers';

interface Props {
  id: string;
  children: ReactElement<{
    disabled?: boolean;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
  }>;
  onYes: () => void;
  buttonName: string;
  disabled?: boolean;
  shouldPrompt?: boolean;
  text?: string;
  buttonText?: string;
  buttonType?: ButtonProps['type'];
}

const AreYouSure = ({
  id,
  children,
  onYes,
  buttonName,
  disabled = false,
  shouldPrompt = true,
  text = 'Are you sure you want to delete this item?',
  buttonText = 'Delete',
  buttonType = 'button',
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const dispatch = useDispatch();

  const dispatchCreatePopover = (e) => {
    setAnchorEl(e.currentTarget);
    dispatch(createPopover(id));
  };

  const dispatchDestroyPopover = useCallback(() => {
    setAnchorEl(null);
    dispatch(destroyPopover(id));
  }, [id, dispatch]);

  const showPopover = useSelector((state: Store) => !!state.popovers[id]);

  useEffect(() => dispatchDestroyPopover, [dispatchDestroyPopover]);

  return (
    <div>
      <span>
        {cloneElement(children, {
          disabled: showPopover || disabled,
          onClick: shouldPrompt ? dispatchCreatePopover : onYes,
        })}
      </span>
      <Popper
        id={id}
        open={showPopover}
        anchorEl={anchorEl}
        placement="top"
        className="shared-are-you-sure-popper"
      >
        <div className="shared-are-you-sure">
          {text}
          <div className="shared-are-you-sure-actions-container">
            <div className="shared-are-you-sure-nevermind">
              <FormButton
                color="alternate"
                margin={false}
                outline
                onClick={(e): void => {
                  e.preventDefault();
                  e.stopPropagation();

                  dispatchDestroyPopover();
                }}
                buttonName={`${buttonName} are-you-sure never-mind`}
                width={110}
                className="shared-are-you-sure-button"
              >
                Nevermind
              </FormButton>
            </div>
            <div className="shared-are-you-sure-yes">
              <FormButton
                color="primary"
                margin={false}
                onClick={(e): void => {
                  e.preventDefault();
                  e.stopPropagation();

                  onYes();
                  dispatchDestroyPopover();
                }}
                buttonName={`${buttonName} are-you-sure yes`}
                type={buttonType}
                className="shared-are-you-sure-button"
                testId="confirm-are-you-sure"
              >
                {buttonText}
              </FormButton>
            </div>
          </div>
        </div>
      </Popper>
    </div>
  );
};

export default AreYouSure;
